import React, { useState, useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Modal, Button, Table, Badge, Alert, Spinner, Row, Col, Form } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useAssignReceipt, useReceiptMatches, useUpdateReceiptScan, useUnmatchReceiptScan } from 'api/hooks/use-receipt-scans'
import { useQueryClient } from 'react-query'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheckCircle, faChevronLeft, faChevronRight, faFilter, faTimes } from '@fortawesome/free-solid-svg-icons'
import { Document, Page, pdfjs } from 'react-pdf'
import { baseUrl } from 'api/helpers'
import { formatDateLocal } from 'helpers/datetime'

import 'react-pdf/dist/Page/AnnotationLayer.css'
import 'react-pdf/dist/Page/TextLayer.css'

pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString()

// Custom style for xxl modal
const modalXxlStyle = `
  .modal-xxl {
    max-width: 95vw;
    width: 1400px;
  }
`

const EditableFilterField = ({ label, name, type, filterKey, matched, step, maxLength, placeholder, value, isActive, onChange, onToggleFilter, showFilter = true }) => {
  const hasValue = value && value !== ''

  return (
    <div className="d-flex align-items-center mb-2">
      <div className="d-flex align-items-center gap-1" style={{ width: '33%', flexShrink: 0 }}>
        <small className="text-muted text-nowrap">
          {label}
        </small>
        {showFilter && hasValue && (
          <Button
            variant={isActive ? 'primary' : 'light'}
            size="sm"
            className="py-0 px-1 flex-shrink-0"
            onClick={() => onToggleFilter(filterKey)}
            title={isActive ? 'Remove exact match filter' : 'Filter to exact matches only'}
          >
            <FontAwesomeIcon icon={isActive ? faTimes : faFilter} size="xs" />
          </Button>
        )}
        {matched && (
          <FontAwesomeIcon icon={faCheckCircle} className="text-success flex-shrink-0" size="sm" />
        )}
      </div>
      <Form.Control
        size="sm"
        type={type || 'text'}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder || ''}
        step={step}
        maxLength={maxLength}
      />
    </div>
  )
}

const ReceiptMatchingModal = ({ show, onHide, receiptScan, receipts = [], onChangeReceipt, scanType = 'receipt' }) => {
  const isInvoice = scanType === 'invoice'
  const [selectedTransactionId, setSelectedTransactionId] = useState(null)
  const [offset, setOffset] = useState(0)
  const [loadedPages, setLoadedPages] = useState([])
  const [filters, setFilters] = useState({})
  const [numPages, setNumPages] = useState(null)
  const [pageNumber, setPageNumber] = useState(1)
  const [pdfLoading, setPdfLoading] = useState(true)
  const [pdfError, setPdfError] = useState(null)
  const [pdfScale, setPdfScale] = useState(1.0)
  const [ocrForm, setOcrForm] = useState({
    user_vendor_name: '',
    user_total_amount: '',
    user_transaction_date: '',
    user_card_last_four: '',
    user_invoice_number: '',
    user_po_number: '',
    user_due_date: ''
  })
  const [ocrDirty, setOcrDirty] = useState(false)
  const limit = 10
  const queryClient = useQueryClient()

  const currentIndex = receipts.findIndex((r) => r.id === receiptScan?.id)
  const hasPrev = currentIndex > 0
  const hasNext = currentIndex < receipts.length - 1

  const goToReceipt = (direction) => {
    const nextIndex = currentIndex + direction
    if (nextIndex >= 0 && nextIndex < receipts.length && onChangeReceipt) {
      onChangeReceipt(receipts[nextIndex])
    }
  }

  const {
    data: matchesData,
    isLoading,
    isError,
    refetch,
    isFetching
  } = useReceiptMatches({
    receiptScanId: receiptScan?.id,
    limit,
    offset,
    filters,
    options: {
      enabled: show && !!receiptScan?.id,
      keepPreviousData: true
    }
  })

  const updateOcrMutation = useUpdateReceiptScan({
    options: {
      onSuccess: () => {
        toast.success('OCR data updated successfully')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['receipt-scan', receiptScan.id])
        queryClient.invalidateQueries(['receipt-matches'])
        setOcrDirty(false)
        setOffset(0)
        setLoadedPages([])
        refetch()
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message || 'Failed to update OCR data'
        toast.error(errorMessage)
      }
    }
  })

  // Reset state when modal opens with new receipt
  useEffect(() => {
    if (show && receiptScan?.id) {
      setOffset(0)
      setLoadedPages([])
      setSelectedTransactionId(null)
      setFilters({})
      setPageNumber(1)
      setPdfLoading(true)
      setPdfError(null)
      setPdfScale(1.0)
      setOcrForm({
        user_vendor_name: receiptScan.effective_vendor_name || receiptScan.ocr_vendor_name || '',
        user_total_amount: receiptScan.effective_total_amount || receiptScan.ocr_total_amount || '',
        user_transaction_date: receiptScan.effective_transaction_date || receiptScan.ocr_transaction_date || '',
        user_card_last_four: receiptScan.effective_card_last_four || receiptScan.ocr_card_last_four || '',
        user_invoice_number: receiptScan.effective_invoice_number || receiptScan.ocr_invoice_number || '',
        user_po_number: receiptScan.effective_po_number || receiptScan.ocr_po_number || '',
        user_due_date: receiptScan.effective_due_date || receiptScan.ocr_due_date || ''
      })
      setOcrDirty(false)
    }
  }, [show, receiptScan?.id])

  // Derive candidates synchronously from query data + previously-loaded pages.
  // Deriving (instead of syncing via useEffect) avoids a render where matchesData
  // has arrived but an effect hasn't yet populated candidate state — which used to
  // briefly show "No matching transactions" until refresh.
  const allCandidates = useMemo(() => {
    const current = matchesData?.candidates || []
    if (offset === 0) return current
    const existingIds = new Set(loadedPages.map((c) => c.id))
    const newCandidates = current.filter((c) => !existingIds.has(c.id))
    return [...loadedPages, ...newCandidates]
  }, [matchesData, offset, loadedPages])

  const assignReceiptMutation = useAssignReceipt({
    options: {
      onSuccess: () => {
        toast.success(isInvoice ? 'Invoice matched successfully' : 'Receipt matched successfully')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['receipt-scan', receiptScan.id])
        queryClient.invalidateQueries(['receipt-matches'])
        queryClient.invalidateQueries(['transactions'])
        // Advance to next receipt if available, otherwise close
        if (hasNext) {
          goToReceipt(1)
        } else {
          onHide()
        }
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message || (isInvoice ? 'Failed to match invoice' : 'Failed to match receipt')
        toast.error(errorMessage)
      }
    }
  })

  const unmatchReceiptMutation = useUnmatchReceiptScan({
    options: {
      onSuccess: (data) => {
        toast.success('Receipt unmatched successfully')
        queryClient.invalidateQueries(['receipt-scans'])
        queryClient.invalidateQueries(['receipt-scan', receiptScan.id])
        queryClient.invalidateQueries(['receipt-matches'])
        queryClient.invalidateQueries(['transactions'])
        // Update the receipt with fresh data from response
        if (data?.receipt_scan && onChangeReceipt) {
          onChangeReceipt(data.receipt_scan)
        }
        // Reset state and refetch candidates
        setOffset(0)
        setLoadedPages([])
        setSelectedTransactionId(null)
        refetch()
      },
      onError: (error) => {
        const errorMessage =
          error?.response?.data?.message || 'Failed to unmatch receipt'
        toast.error(errorMessage)
      }
    }
  })

  const handleAssign = () => {
    if (!selectedTransactionId) {
      toast.error('Please select a transaction')
      return
    }

    const params = { receiptScanId: receiptScan.id }
    if (isInvoice) {
      params.apTransactionDetailId = selectedTransactionId
    } else {
      params.pcTransactionDetailId = selectedTransactionId
    }
    assignReceiptMutation.mutate(params)
  }

  const handleUnmatch = () => {
    unmatchReceiptMutation.mutate(receiptScan.id)
  }

  const handleLoadMore = () => {
    setLoadedPages(allCandidates)
    setOffset((prev) => prev + limit)
  }

  const handleRefresh = () => {
    setOffset(0)
    setLoadedPages([])
    refetch()
  }

  const handleOcrChange = (e) => {
    const { name, value } = e.target
    setOcrForm((prev) => ({ ...prev, [name]: value }))
    setOcrDirty(true)
  }

  const handleOcrSave = () => {
    updateOcrMutation.mutate({
      id: receiptScan.id,
      data: ocrForm
    })
  }

  const toggleFilter = (filterName) => {
    setOffset(0)
    setLoadedPages([])
    setFilters((prev) => {
      const newFilters = { ...prev }
      if (newFilters[filterName]) {
        delete newFilters[filterName]
      } else {
        newFilters[filterName] = true
      }
      return newFilters
    })
  }

  const clearAllFilters = () => {
    setOffset(0)
    setLoadedPages([])
    setFilters({})
  }

  const getStatusBadge = (status) => {
    if (status === 'processing') {
      return (
        <Badge bg="" className="badge-info" style={{ fontSize: '0.7rem' }}>
          <Spinner animation="border" size="sm" className="me-1" style={{ width: '0.6rem', height: '0.6rem' }} />
          Processing
        </Badge>
      )
    }
    const statusMap = {
      pending: { className: 'badge-secondary', text: 'Pending' },
      matched: { className: 'badge-primary', text: 'Matched' },
      manual_review: { className: 'badge-warning', text: 'Needs Review' },
      failed: { className: 'badge-danger', text: 'Failed' }
    }
    const config = statusMap[status] || { className: 'badge-secondary', text: status }
    return <Badge bg="" className={config.className} style={{ fontSize: '0.7rem' }}>{config.text}</Badge>
  }

  const getMatchTypeBadge = (matchType) => {
    if (!matchType) return null
    const text = matchType === 'manual' ? 'Manually Matched' : 'Matched'
    return <Badge bg="" className="badge-primary" style={{ fontSize: '0.7rem' }}>{text}</Badge>
  }

  const getConfidenceColor = (score) => {
    if (score >= 0.85) return 'success'
    if (score >= 0.7) return 'warning'
    return 'danger'
  }

  const formatCurrency = (amount) => {
    if (!amount) return 'N/A'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount)
  }

  const getScoreDisplay = (score) => {
    if (score === null || score === undefined) return 'N/A'
    return `${Math.round(score * 100)}%`
  }

  // PDF handlers
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages)
    setPdfLoading(false)
    setPdfError(null)
  }

  const onDocumentLoadError = (error) => {
    console.error('PDF load error:', error)
    setPdfLoading(false)
    setPdfError('Failed to load PDF')
  }

  const goToPrevPage = () => {
    setPageNumber((prev) => Math.max(prev - 1, 1))
  }

  const goToNextPage = () => {
    setPageNumber((prev) => Math.min(prev + 1, numPages))
  }

  const zoomIn = () => {
    setPdfScale((prev) => Math.min(prev + 0.25, 2.5))
  }

  const zoomOut = () => {
    setPdfScale((prev) => Math.max(prev - 0.25, 0.5))
  }

  const resetZoom = () => {
    setPdfScale(1.0)
  }

  if (!receiptScan) return null

  const hasMore = matchesData?.meta?.has_more
  const totalCount = matchesData?.meta?.total_count || 0
  const activeFilterCount = Object.keys(filters).length

  // Get the top candidate's per-field scores to show "Match" badges
  const topScores = allCandidates.length > 0 ? (allCandidates[0].scores || {}) : {}

  // Construct image URL
  const imageUrl = receiptScan.receipt_image_url
    ? `${baseUrl}${receiptScan.receipt_image_url}`
    : null
  const isPdf = receiptScan.file_name?.toLowerCase().endsWith('.pdf')

  return (
    <>
      <style>{modalXxlStyle}</style>
      <Modal show={show} onHide={onHide} centered dialogClassName="modal-xxl">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          {isInvoice ? 'Match Invoice to Transaction' : 'Match Receipt to Transaction'}
          {receipts.length > 1 && (
            <>
              <small className="text-muted" style={{ fontSize: '0.8rem', fontWeight: 'normal' }}>
                ({currentIndex + 1} of {receipts.length})
              </small>
              <div className="d-flex gap-1 ms-1">
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => goToReceipt(-1)}
                  disabled={!hasPrev}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Button>
                <Button
                  variant="light"
                  size="sm"
                  onClick={() => goToReceipt(1)}
                  disabled={!hasNext}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Button>
              </div>
            </>
          )}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '85vh', overflowY: 'auto' }}>
        <Row>
          {/* Left Column - Receipt Image/PDF */}
          <Col lg={6}>
            <div
              className="receipt-preview rounded p-3 text-center"
              style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                position: 'sticky',
                top: 0
              }}
            >
              {imageUrl ? (
                isPdf ? (
                  <div>
                    {pdfLoading && (
                      <div className="d-flex justify-content-center align-items-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <span className="ms-2">Loading PDF...</span>
                      </div>
                    )}

                    {pdfError && (
                      <div className="text-center py-3">
                        <p className="text-danger small mb-2">{pdfError}</p>
                        <Button
                          variant="light"
                          size="sm"
                          onClick={() => window.open(imageUrl, '_blank')}
                        >
                          Open in New Tab
                        </Button>
                      </div>
                    )}

                    <div style={{ overflowX: 'auto', maxWidth: '100%', maxHeight: '60vh', overflowY: 'auto' }}>
                      <Document
                        file={imageUrl}
                        onLoadSuccess={onDocumentLoadSuccess}
                        onLoadError={onDocumentLoadError}
                        loading=""
                      >
                        <Page
                          pageNumber={pageNumber}
                          scale={pdfScale}
                          renderTextLayer={true}
                          renderAnnotationLayer={true}
                        />
                      </Document>
                    </div>

                    {!pdfLoading && !pdfError && (
                      <div className="d-flex justify-content-center align-items-center gap-3 mt-3 flex-wrap">
                        {/* Zoom Controls */}
                        <div className="d-flex align-items-center gap-1">
                          <Button
                            variant="light"
                            size="sm"
                            onClick={zoomOut}
                            disabled={pdfScale <= 0.5}
                            title="Zoom out"
                          >
                            −
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={resetZoom}
                            title="Reset zoom"
                            style={{ minWidth: '60px' }}
                          >
                            {Math.round(pdfScale * 100)}%
                          </Button>
                          <Button
                            variant="light"
                            size="sm"
                            onClick={zoomIn}
                            disabled={pdfScale >= 2.5}
                            title="Zoom in"
                          >
                            +
                          </Button>
                        </div>

                        {/* Page Controls */}
                        {numPages && numPages > 1 && (
                          <div className="d-flex align-items-center gap-1">
                            <Button
                              variant="light"
                              size="sm"
                              onClick={goToPrevPage}
                              disabled={pageNumber <= 1}
                            >
                              Prev
                            </Button>
                            <span className="text-muted small">
                              {pageNumber} / {numPages}
                            </span>
                            <Button
                              variant="light"
                              size="sm"
                              onClick={goToNextPage}
                              disabled={pageNumber >= numPages}
                            >
                              Next
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <img
                      src={imageUrl}
                      alt="Receipt"
                      className="img-fluid rounded"
                      style={{ maxHeight: '60vh' }}
                    />
                  </div>
                )
              ) : (
                <div className="py-5 text-muted">
                  No {isInvoice ? 'invoice' : 'receipt'} image available
                </div>
              )}
            </div>
          </Col>

          {/* Right Column - OCR Data & Candidates */}
          <Col lg={6}>
            {/* OCR Extracted Data */}
            <div className="mb-3">
              {receiptScan.file_name && (
                <div className="d-flex align-items-center mb-2">
                  <div style={{ width: '33%', flexShrink: 0 }}><small className="text-muted">File Name</small></div>
                  <span>{receiptScan.file_name}</span>
                </div>
              )}
              <div className="d-flex align-items-center mb-2">
                <div style={{ width: '33%', flexShrink: 0 }}><small className="text-muted">Status</small></div>
                <div className="d-flex align-items-center gap-2">
                  {receiptScan.match_type
                    ? getMatchTypeBadge(receiptScan.match_type)
                    : receiptScan.status && getStatusBadge(receiptScan.status)
                  }
                  {activeFilterCount > 0 && (
                    <Badge bg="primary">
                      {activeFilterCount} filter{activeFilterCount > 1 ? 's' : ''}
                    </Badge>
                  )}
                </div>
              </div>
              <hr className="my-2" />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Extracted Information</h6>
              </div>
              <div>
                {receiptScan.status !== 'matched' && (
                  <small className="text-muted d-block mb-2">
                    <FontAwesomeIcon icon={faFilter} className="me-1" />
                    Edit fields and click filter icon for exact matches
                  </small>
                )}
                <EditableFilterField
                  label="Amount"
                  name="user_total_amount"
                  type="number"
                  step="0.01"
                  filterKey="exact_amount"
                  matched={topScores.amount >= 0.85}
                  placeholder="0.00"
                  value={ocrForm.user_total_amount}
                  isActive={filters.exact_amount}
                  onChange={handleOcrChange}
                  onToggleFilter={toggleFilter}
                  showFilter={receiptScan.status !== 'matched'}
                />
                <EditableFilterField
                  label={isInvoice ? 'Invoice Date' : 'Date'}
                  name="user_transaction_date"
                  type="date"
                  filterKey="exact_date"
                  matched={topScores.date >= 0.85}
                  value={ocrForm.user_transaction_date}
                  isActive={filters.exact_date}
                  onChange={handleOcrChange}
                  onToggleFilter={toggleFilter}
                  showFilter={receiptScan.status !== 'matched'}
                />
                <EditableFilterField
                  label="Vendor"
                  name="user_vendor_name"
                  filterKey="exact_vendor"
                  matched={topScores.vendor_name >= 0.85}
                  placeholder="Vendor name"
                  value={ocrForm.user_vendor_name}
                  isActive={filters.exact_vendor}
                  onChange={handleOcrChange}
                  onToggleFilter={toggleFilter}
                  showFilter={receiptScan.status !== 'matched'}
                />
                {isInvoice && (
                  <>
                    <EditableFilterField
                      label="Invoice #"
                      name="user_invoice_number"
                      filterKey="exact_invoice"
                      matched={topScores.invoice_number >= 0.85}
                      placeholder="Invoice number"
                      value={ocrForm.user_invoice_number}
                      isActive={filters.exact_invoice}
                      onChange={handleOcrChange}
                      onToggleFilter={toggleFilter}
                      showFilter={receiptScan.status !== 'matched'}
                    />
                    <EditableFilterField
                      label="PO #"
                      name="user_po_number"
                      filterKey="exact_po"
                      matched={topScores.po_number >= 0.85}
                      placeholder="PO number"
                      value={ocrForm.user_po_number}
                      isActive={filters.exact_po}
                      onChange={handleOcrChange}
                      onToggleFilter={toggleFilter}
                      showFilter={receiptScan.status !== 'matched'}
                    />
                  </>
                )}
                {ocrDirty && (
                  <div className="d-flex align-items-center mt-2">
                    <div style={{ width: '33%', flexShrink: 0 }}></div>
                    <Button
                      variant="primary"
                      size="sm"
                      className="px-4"
                      onClick={handleOcrSave}
                      disabled={updateOcrMutation.isLoading}
                    >
                      {updateOcrMutation.isLoading ? <Spinner size="sm" /> : 'Save'}
                    </Button>
                  </div>
                )}
              </div>
            </div>

            <hr className="my-2" />

            <div className="d-flex gap-1 mb-2">
              <Button
                variant="light"
                size="sm"
                onClick={handleRefresh}
                disabled={isFetching}
              >
                {isFetching ? <Spinner size="sm" /> : 'Refresh'}
              </Button>
              {activeFilterCount > 0 && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={clearAllFilters}
                >
                  Clear Filters
                </Button>
              )}
            </div>

            {/* Matched Transaction or Candidate Transactions */}
            {receiptScan.status === 'matched' && receiptScan.match_candidates?.[0] ? (
              <div>
                <h6 className="mb-2">
                  Matched Transaction{' '}
                  <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].total_score)}`} style={{ fontSize: '0.85rem' }}>
                    {getScoreDisplay(receiptScan.match_candidates[0].total_score)}
                  </span>
                </h6>
                <Table bordered size="sm" className="small mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: '30%' }}>Field</th>
                      <th>Value</th>
                      <th style={{ width: '70px' }}>Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-muted">Amount</td>
                      <td>{formatCurrency(receiptScan.match_candidates[0].matched_transaction?.amount)}</td>
                      <td>
                        {receiptScan.match_candidates[0].scores?.amount != null && (
                          <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].scores.amount)}`}>
                            {getScoreDisplay(receiptScan.match_candidates[0].scores.amount)}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Date</td>
                      <td>{formatDateLocal(receiptScan.match_candidates[0].matched_transaction?.date)}</td>
                      <td>
                        {receiptScan.match_candidates[0].scores?.date != null && (
                          <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].scores.date)}`}>
                            {getScoreDisplay(receiptScan.match_candidates[0].scores.date)}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-muted">Vendor</td>
                      <td>{receiptScan.match_candidates[0].matched_transaction?.vendor_name || 'N/A'}</td>
                      <td>
                        {receiptScan.match_candidates[0].scores?.vendor_name != null && (
                          <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].scores.vendor_name)}`}>
                            {getScoreDisplay(receiptScan.match_candidates[0].scores.vendor_name)}
                          </span>
                        )}
                      </td>
                    </tr>
                    {isInvoice ? (
                      <>
                        <tr>
                          <td className="text-muted">Invoice #</td>
                          <td>{receiptScan.match_candidates[0].matched_transaction?.invoice_number || 'N/A'}</td>
                          <td>
                            {receiptScan.match_candidates[0].scores?.invoice_number != null && (
                              <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].scores.invoice_number)}`}>
                                {getScoreDisplay(receiptScan.match_candidates[0].scores.invoice_number)}
                              </span>
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="text-muted">PO #</td>
                          <td>{receiptScan.match_candidates[0].matched_transaction?.po_number || 'N/A'}</td>
                          <td>
                            {receiptScan.match_candidates[0].scores?.po_number != null && (
                              <span className={`text-${getConfidenceColor(receiptScan.match_candidates[0].scores.po_number)}`}>
                                {getScoreDisplay(receiptScan.match_candidates[0].scores.po_number)}
                              </span>
                            )}
                          </td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td className="text-muted">Employee</td>
                        <td>{receiptScan.match_candidates[0].matched_transaction?.employee_name || 'N/A'}</td>
                        <td></td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            ) : isLoading && offset === 0 ? (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
                <div className="mt-2 small">Searching for matches...</div>
              </div>
            ) : isError ? (
              <Alert variant="danger" className="small">
                Failed to load matching transactions.
              </Alert>
            ) : allCandidates.length === 0 ? (
              <Alert variant="warning" className="small">
                {activeFilterCount > 0 ? (
                  <>
                    No matches with current filters.{' '}
                    <Button variant="link" className="p-0" onClick={clearAllFilters}>
                      Clear filters
                    </Button>
                  </>
                ) : (
                  'No matching transactions found.'
                )}
              </Alert>
            ) : (
              <div>
                <h6 className="mb-2">
                  Candidates{' '}
                  <small className="text-muted">
                    ({allCandidates.length} of {totalCount})
                  </small>
                </h6>
                <div style={{ maxHeight: '45vh', overflowY: 'auto' }}>
                  <Table striped bordered hover size="sm" className="small">
                    <thead>
                      <tr>
                        <th style={{ width: '35px' }}></th>
                        <th>Score</th>
                        <th>Amount</th>
                        <th>{isInvoice ? 'Invoice Date' : 'Date'}</th>
                        <th>Vendor</th>
                        {isInvoice && <th>Invoice #</th>}
                        {isInvoice && <th>PO #</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {allCandidates.map((candidate) => (
                        <tr
                          key={candidate.id}
                          onClick={() => setSelectedTransactionId(candidate.id)}
                          style={{ cursor: 'pointer' }}
                          className={
                            selectedTransactionId === candidate.id ? 'table-active' : ''
                          }
                        >
                          <td className="text-center">
                            <input
                              type="radio"
                              name="transaction"
                              checked={selectedTransactionId === candidate.id}
                              onChange={() => setSelectedTransactionId(candidate.id)}
                            />
                          </td>
                          <td>
                            <Badge bg={getConfidenceColor(candidate.score)}>
                              {getScoreDisplay(candidate.score)}
                            </Badge>
                          </td>
                          <td>{formatCurrency(candidate.amount)}</td>
                          <td>{formatDateLocal(candidate.date)}</td>
                          <td>{candidate.vendor_name || 'N/A'}</td>
                          {isInvoice && <td>{candidate.invoice_number || 'N/A'}</td>}
                          {isInvoice && <td>{candidate.po_number || 'N/A'}</td>}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                {hasMore && (
                  <div className="text-center mt-2">
                    <Button
                      variant="light"
                      size="sm"
                      onClick={handleLoadMore}
                      disabled={isFetching}
                    >
                      {isFetching ? <Spinner size="sm" /> : `Load More (${allCandidates.length}/${totalCount})`}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="light" onClick={onHide}>
          Cancel
        </Button>
        {receiptScan.status === 'matched' ? (
          <Button
            variant="outline-danger"
            onClick={handleUnmatch}
            disabled={unmatchReceiptMutation.isLoading}
          >
            {unmatchReceiptMutation.isLoading ? 'Unmatching...' : 'Unmatch'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={handleAssign}
            disabled={!selectedTransactionId || assignReceiptMutation.isLoading}
          >
            {assignReceiptMutation.isLoading ? 'Assigning...' : 'Assign to Selected'}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
    </>
  )
}

ReceiptMatchingModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  receiptScan: PropTypes.object,
  receipts: PropTypes.array,
  onChangeReceipt: PropTypes.func,
  scanType: PropTypes.string
}

export default ReceiptMatchingModal
